// frontend/src/utils/syntaxHint.js
// ref FQE-6 + Phase E (phrase hint UI): ConditionRow / SearchBar keyword syntax hint helpers

/**
 * Detect FTS5 syntax chars that backend will strip from a search keyword.
 *
 * Mirrors backend/src/services/search/searchEngine.js `_sanitizeFtsTerm`
 *   regex `/[^\p{Script=Han}a-zA-Z0-9_]/gu`
 *
 * 兩處差異與理由：frontend 額外排除 `\s` — backend pipeline 在 `_sanitizeFtsTerm`
 * 之前已先 `cutWords` (jieba) 把 whitespace 切成 segment 邊界，whitespace 永不
 * 進入後端 sanitize；frontend 直接看 user raw input 必須額外排除以對齊 user 視角的
 * 「最終 NEAR 鄰近搜尋」結果（pipeline: trim → NFKC → cutWords → per-segment sanitize）。
 *
 * Pipeline invariant：若 backend 改成不走 cutWords 直接送 keyword 進 sanitize，
 * 此 helper 會誤導 user（whitespace 將被 strip 但 hint 不警告）。invariant 變動時
 * 必須同步調整此 regex（drift defence: backend `_sanitizeFtsTerm` JSDoc cross-ref +
 * 此檔 unit test 紅綠 mutation guard）。
 *
 * Layer A NFKC normalization (Compat U+F900+ → base codepoint) 在 server-side
 * 透明發生，UI 不需顯示。此 helper 只給 ConditionRow 進階搜尋的 keyword 輸入。
 *
 * @param {string} input - User-typed keyword
 * @returns {string[]} 去重後的 stripped 字元陣列（保留首見順序）
 */
export const detectStrippedChars = (input) => {
  if (typeof input !== 'string' || input.length === 0) return [];
  const matches = input.match(/[^\p{Script=Han}a-zA-Z0-9_\s]/gu);
  if (!matches) return [];
  return [...new Set(matches)];
};

/**
 * 格式化 stripped chars 為中文 helperText。
 *
 * @param {string[]} chars
 * @returns {string} '' (no chars) or '將忽略：「c1」、「c2」'
 */
export const formatStrippedHint = (chars) => {
  if (!Array.isArray(chars) || chars.length === 0) return '';
  return `將忽略：${chars.map((c) => `「${c}」`).join('、')}`;
};

/**
 * Phase E (internal): 偵測 phrase form `^"([^"]*)"$`，回傳 form/content 配對。
 *
 * 單一 regex source — `detectPhrase` + `formatHint` 兩 caller 共用此 helper，
 * 避免兩處 regex pattern + trim 邏輯漂移（drift defence）。
 *
 * @param {string} input
 * @returns {{form: boolean, content: string}}
 *   form=true 表示 raw input trim 後符合 `"..."` 形式（content 可能為空白）；
 *   form=false 表示非 phrase form。
 */
const _phraseFormMatch = (input) => {
  if (typeof input !== 'string' || input.length === 0) {
    return { form: false, content: '' };
  }
  const trimmed = input.trim();
  const m = trimmed.match(/^"([^"]*)"$/);
  if (!m) return { form: false, content: '' };
  return { form: true, content: m[1] };
};

/**
 * Phase E: 偵測純 phrase 形式（半形雙引號完整包覆，內容非空白）。
 *
 * 對齊後端 `_buildFtsFromConditions` (searchEngine.js Phase D) 的
 * `^"([^"]*)"$` 偵測 + early trim + empty content 拒絕。
 *
 * @param {string} input - User-typed keyword
 * @returns {string|null} phrase content（已 trim 外層空白後抽出引號內容）或 null
 *   - 非 phrase form → null
 *   - phrase form 但 content 為純空白 → null（與非 phrase form 同 sentinel）
 */
export const detectPhrase = (input) => {
  const { form, content } = _phraseFormMatch(input);
  if (!form || content.trim().length === 0) return null;
  return content;
};

/**
 * Phase E: 整合 hint formatter — 同時偵測 phrase 與 strip，按優先序回 hint 物件。
 *
 * 優先序：strip 警告 > phrase 提示 > 無
 *  - phrase form 命中（_phraseFormMatch.form=true）：
 *      - content 純空白 → none（外層引號合法 syntax，不報 strip warning）
 *      - content 含 strip 字元 → 警告（phrase 失敗訊息誤導，警告優先）
 *      - content 乾淨 → 綠色 phrase 提示
 *  - 非 phrase form：對 raw input 偵測 strip
 *      - 命中（含 malformed quote）→ 警告
 *      - 否則 → 無 hint
 *
 * 回傳格式統一物件，便於 caller 動態切換 helperText sx.color。
 *
 * @param {string} input - User-typed keyword
 * @returns {{type: 'none'|'phrase'|'strip', text: string, color: string}}
 */
export const formatHint = (input) => {
  const none = { type: 'none', text: '', color: '' };
  if (typeof input !== 'string' || input.length === 0) return none;

  const { form, content } = _phraseFormMatch(input);

  if (form) {
    if (content.trim().length === 0) return none;

    const stripped = detectStrippedChars(content);
    if (stripped.length > 0) {
      return {
        type: 'strip',
        text: formatStrippedHint(stripped),
        color: 'warning.main',
      };
    }
    return {
      type: 'phrase',
      text: '✅ 精確片語匹配',
      color: 'success.main',
    };
  }

  // 非 phrase form：strip 偵測對整 raw input（malformed quote 也算被剝字元）
  const stripped = detectStrippedChars(input);
  if (stripped.length > 0) {
    return {
      type: 'strip',
      text: formatStrippedHint(stripped),
      color: 'warning.main',
    };
  }
  return none;
};
